import { useMutation } from '@tanstack/react-query';
import { findIndex } from 'lodash';
import { produce } from 'immer';
import { Page, SpacerWidget } from '../../types';
import { handleMutationSuccess, updateGuideVersion } from '../helpers';
import { GUIDE_KEYS } from '../../queries/guides';
import { SpacingWidgetOptions } from '../../constants/widgets';
import { queryClient } from '../../constants/app';
import { useGuideStore } from '../../stores/guideStore';
import {
  editDividerInSpacingWidget,
  editHeightInSpacingWidget,
  editStyleInSpacingWidget,
} from '../../helpers/api/widgets/spacing';

export const useSpacerWidgetMutations = (
  sectionId: string,
  columnIndex: number,
  widgetId: string,
) => {
  const activeGuideId = useGuideStore(s => s.activeGuideId) as string;
  const activePageId = useGuideStore(s => s.activePageId) as string;
  const activeChapterId = useGuideStore(s => s.activeChapterId) as string;
  const activeGuideVersion = useGuideStore(s => s.activeGuideVersion) as number;

  return {
    editStyleInSpacerWidget: useMutation(
      (options: { newStyle: string }) => {
        return editStyleInSpacingWidget(
          activeGuideId,
          activeChapterId,
          activePageId,
          sectionId,
          widgetId,
          columnIndex,
          options.newStyle,
          activeGuideVersion,
        );
      },
      {
        onMutate: variables => {
          updateGuideVersion(activeGuideId);
          optimisticallyUpdateSpacerWidget({
            guideId: activeGuideId,
            pageId: activePageId,
            sectionId,
            columnIndex,
            widgetId,
            properties: {
              style: {
                stylesheet: variables.newStyle,
                version: 3,
                language: 'css',
              },
            },
          });
        },
        onSuccess: data => {
          handleMutationSuccess('Spacer Widget Edited', data, {
            entityId: activeGuideId,
          });
        },
      },
    ),
    updateSpacerWidget: useMutation(
      (options: { widgetDivider: SpacingWidgetOptions }) => {
        return editDividerInSpacingWidget(
          activeGuideId,
          activeChapterId,
          activePageId,
          sectionId,
          widgetId,
          columnIndex,
          options.widgetDivider,
          activeGuideVersion,
        );
      },
      {
        onMutate: variables => {
          updateGuideVersion(activeGuideId);
          optimisticallyUpdateSpacerWidget({
            guideId: activeGuideId,
            pageId: activePageId,
            sectionId,
            columnIndex,
            widgetId,
            properties: { divider: variables.widgetDivider },
          });
        },
        onSuccess: data => {
          handleMutationSuccess('Spacer Widget Edited', data, {
            entityId: activeGuideId,
          });
        },
      },
    ),
    resizeSpacerWidget: useMutation(
      (options: { widgetHeight: number }) =>
        editHeightInSpacingWidget(
          activeGuideId,
          activeChapterId,
          activePageId,
          sectionId,
          columnIndex,
          widgetId,
          options.widgetHeight,
          activeGuideVersion,
        ),
      {
        onMutate: variables => {
          updateGuideVersion(activeGuideId);
          optimisticallyUpdateSpacerWidget({
            guideId: activeGuideId,
            pageId: activePageId,
            sectionId,
            columnIndex,
            widgetId,
            properties: { height: variables.widgetHeight },
          });
        },
        onSuccess: data => {
          handleMutationSuccess('Spacer Widget Edited', data, {
            entityId: activeGuideId,
          });
        },
      },
    ),
  };
};

const optimisticallyUpdateSpacerWidget = ({
  guideId,
  pageId,
  sectionId,
  columnIndex,
  widgetId,
  properties,
}: {
  guideId: string;
  pageId: string;
  sectionId: string;
  columnIndex: number;
  widgetId: string;
  properties: Partial<SpacerWidget>;
}) => {
  queryClient.setQueryData(
    [GUIDE_KEYS.PAGE_BY_ID, guideId, pageId],
    (old: Page) => {
      const sectionIndex = findIndex(
        old.sections,
        section => section.id === sectionId,
      );
      const widgetIndex = findIndex(
        old.sections[sectionIndex].columns[columnIndex].widgets,
        widget => widget.id === widgetId,
      );
      const newWidget: SpacerWidget = {
        ...old.sections[sectionIndex].columns[columnIndex].widgets[widgetIndex],
        ...properties,
      };

      if (old) {
        return produce(old, draft => {
          draft.sections[sectionIndex].columns[columnIndex].widgets[
            widgetIndex
          ] = newWidget;
        });
      }

      return old;
    },
  );
};
